<script lang="ts">
import {defineComponent, onMounted, ref} from "vue";
import {translation, useTranslationStore} from "~/plugins/store/translations";
import type {TranslationList} from "~/types/Utils";
import MasterLayout from "~/layouts/MasterLayout.vue";
import {useTwoFactorStore} from "~/plugins/store/two-factor";
import {type TwoFactorProvider, type VerifyTwoFactorCodeResponse} from "~/types/TwoFactor";
import {useAuthStore} from "~/plugins/store/auth";
import {recaptcha} from "~/composables/recaptcha";
import {useReCaptcha} from "vue-recaptcha-v3";
import {useNuxtApp} from "#app";
import AuthTwoFactorCode from "~/components/auth/AuthTwoFactorCode.vue";
import LoadingDialog from "~/components/utils/LoadingDialog.vue";
import {useSystemStore} from "~/plugins/store/system";
import AccountLayout from "~/layouts/AccountLayout.vue";

export default defineComponent({
  components: {AccountLayout, LoadingDialog, AuthTwoFactorCode, MasterLayout},
  setup() {
    definePageMeta({
      middleware: [
        'auth',
      ],
    });

    useHead({
      title: 'XIGA | 2FA Check',
      meta: [
        {
          name: 'description',
          content: 'META'
        }
      ],
    });

    const showLoadingDialog = ref(false);
    const messageLoadingDialog = ref('');
    const route = useRoute();
    const router = useRouter();
    const {$toast} = useNuxtApp();
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({}); // Initialize translations as a reactive reference
    const lastUrl = ref<string | null>('');
    const disableAction = ref(false);
    const twoFactorStore = useTwoFactorStore();
    const verifyTwoFactorCodeResponse = ref<VerifyTwoFactorCodeResponse | null>(null);
    const authStore = useAuthStore();
    const recaptchaInstance = useReCaptcha();
    const twoFactorCode = ref<string | null>(null);
    const activeStep = ref(1);
    const isTwoFactorCodeDisabled = ref(false);
    const recoveryKey = ref<string>('');
    const twoFactorProvider = ref<TwoFactorProvider | null>(null);

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');

      lastUrl.value = route.query.last_url as string;

      if (route.query.disable) {
        disableAction.value = true;
      }
      // Verifică dacă URL-ul curent conține parametrul și elimină-l dacă este cazul
      const currentPath = window.location.pathname;
      const basePath = '/account/2fa-check'; // Path-ul de bază pe care îl dorești fără parametri
      if (currentPath.startsWith(`${basePath}`)) {
        // Asigură-te că rămâne doar calea de bază
        const newUrl = `${window.location.origin}${basePath}`;

        // Păstrează starea existentă din history.state
        const currentState = history.state;
        history.replaceState(currentState, '', newUrl);
      }

      const token = await recaptcha(recaptchaInstance);
      twoFactorProvider.value = await twoFactorStore.fetchUserTwoFactorProvider(<string>authStore.getAccessToken(), token);
      await generateCode();
    });


    watch(() => route.fullPath, async (newVal, oldVal) => {
      const currentPath = window.location.pathname;
      const basePath = '/account/2fa-check'; // Path-ul de bază pe care îl dorești fără parametri
      if (currentPath.startsWith(`${basePath}`)) {
        // Asigură-te că rămâne doar calea de bază
        const newUrl = `${window.location.origin}${basePath}`;

        // Păstrează starea existentă din history.state
        const currentState = history.state;
        history.replaceState(currentState, '', newUrl);
      }
    });

    const generateCode = async () => {
      if (twoFactorProvider.value && (twoFactorProvider.value.code === 'email' || twoFactorProvider.value.code === 'sms')) {
        showLoadingDialog.value = true;
        messageLoadingDialog.value = translation(translationList.value.loading, 'Se încarcă...');
        const token = await recaptcha(recaptchaInstance);
        const {userAgent} = useDevice();
        const systemStore = useSystemStore();
        const response = await twoFactorStore.generateTwoFactorCode(
            <string>authStore.getAccessToken(),
            userAgent,
            <string>systemStore.clientIp,
            twoFactorProvider.value.code,
            token);
        if (response && response.is_sent) {
          $toast.success(translation(translationList.value.email_sent, 'Codul de verificare a fost trimis.'));
        } else {
          $toast.error(translation(translationList.value.email_not_sent, 'Codul de verificare nu a putut fi trimis.'));
        }
        showLoadingDialog.value = false;
      }
    }

    const validateCode = async (shouldRemoveTwoFactor: boolean = false) => {
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.two_factor_code_checking, 'Validăm codul introdus...');
      if (twoFactorCode.value !== null) {
        const token = await recaptcha(recaptchaInstance);
        const {userAgent} = useDevice();
        const systemStore = useSystemStore();
        verifyTwoFactorCodeResponse.value = await twoFactorStore.verifyTwoFactorCode(
            <string>authStore.getAccessToken(),
            twoFactorCode.value,
            <string>twoFactorProvider.value?.code,
            userAgent,
            <string>systemStore.clientIp,
            token);
        if (verifyTwoFactorCodeResponse.value?.is_valid) {
          isTwoFactorCodeDisabled.value = true;
          $toast.success(translation(translationList.value.two_factor_code_valid, 'Autentificarea in doi pasi a fost verificata cu succes.'));

          //shouldRemoveTwoFactor
          if (disableAction) {
            activeStep.value = 2;
            isTwoFactorCodeDisabled.value = false;
            showLoadingDialog.value = false;
            return;
          } else {
            messageLoadingDialog.value = translation(translationList.value.two_factor_code_valid, 'Te redirectionam...');
            //wait for 2 seconds before redirecting
            setTimeout(() => {
              if (lastUrl.value) {
                window.location.href = lastUrl.value;
              } else {
                window.location.href = '/';
              }
            }, 2000);
          }
        } else {
          showLoadingDialog.value = false;
          $toast.error(translation(translationList.value.two_factor_code_invalid, 'Codul introdus este invalid.'));
        }
      } else {
        showLoadingDialog.value = false;
        $toast.error(translation(translationList.value.two_factor_code_invalid, 'Codul introdus este invalid.'));
      }
    }

    const removeTwoFactor = async () => {
      //validate recovery key
      if (!recoveryKey.value || recoveryKey.value.length === 0) {
        $toast.error(translation(translationList.value.required, 'Acest câmp este obligatoriu'));
        return;
      }

      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.loading, 'Se încarcă...');
      const token = await recaptcha(recaptchaInstance);
      const response = await twoFactorStore.removeTwoFactorAuth(
          <string>authStore.getAccessToken(),
          recoveryKey.value,
          <string>twoFactorProvider.value?.code,
          token);
      if (response) {
        if (authStore.user) {
          authStore.user.is_two_factor_enabled = false;
        }
        $toast.success(translation(translationList.value.two_factor_removed, 'Autentificarea in doi pasi a fost eliminata cu succes.'));
        //wait for 2 seconds before redirecting
        setTimeout(() => {
          navigateTo('/');
        }, 2000);
      } else {
        $toast.error(translation(translationList.value.two_factor_not_removed, 'Autentificarea in doi pasi nu a putut fi eliminata.'));
        showLoadingDialog.value = false;
      }
    }

    const backToStep = (step: number) => {
      activeStep.value = step;
    };

    return {
      translationList,
      translation,
      validateCode,
      twoFactorCode,
      messageLoadingDialog,
      showLoadingDialog,
      verifyTwoFactorCodeResponse,
      lastUrl,
      activeStep,
      removeTwoFactor,
      recoveryKey,
      twoFactorProvider,
      isTwoFactorCodeDisabled,
      backToStep
    }
  }
});
</script>

<template>
  <master-layout class="travel">
    <v-container class="content-container ">
      <v-container fluid class="component-container-800mw">
        <v-container fluid class="centered-horizontally">
          <loading-dialog :message="messageLoadingDialog" :show-dialog="showLoadingDialog"/>
          <v-container class="background-ghost-white" style="max-width: 1200px; border-radius: 15px;">
            <v-card rounded="xl" variant="elevated" elevation="15" class="w-100 pa-5" min-height="400">
              <div v-if="activeStep === 1" class="d-flex flex-column justify-space-between"
                   style="min-height: 500px;">
                <div>
                  <v-row>
                    <v-col cols="12" sm="auto" md="12" class="d-flex flex-column justify-start centered-on-mobile">
                      <h3 class="font-weight-bold">
                        {{
                          translation(translationList.two_factor_check, 'Verificare Autentificare în Doi Pași (2FA)')
                        }}</h3>
                      <p class="font-weight-regular">
                        {{
                          translation(translationList.two_factor_check_helper, 'Autentificarea în doi pași este activă pe contul tău. Introdu codul generat de aplicația de autentificare pentru a continua.')
                        }}
                      </p>
                    </v-col>
                  </v-row>
                </div>


                <div class="flex-grow-1 d-flex align-center">
                  <v-row>
                    <v-col cols="12" sm="12" md="12" class="d-flex flex-column justify-start centered-on-mobile">
                      <auth-two-factor-code v-model="twoFactorCode" :is-disabled="isTwoFactorCodeDisabled"/>
                    </v-col>
                  </v-row>
                </div>

                <div>
                  <v-row class="mb-15">
                    <v-col cols="12" sm="12" md="12" class="d-flex justify-center centered-on-mobile">
                      <v-btn
                          class="block-on-mobile"
                          color="primary"
                          size="x-large"
                          flat
                          rounded="lg"
                          @click="validateCode()"
                      >
                        {{ translation(translationList.validate, 'Validează codul') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
              </div>
              <div v-else class="d-flex flex-column justify-space-between" style="min-height: 500px;">
                <div>
                  <v-row>
                    <v-col cols="12" sm="auto" md="12" class="d-flex flex-column justify-start centered-on-mobile">
                      <h3 class="font-weight-bold">
                        {{
                          translation(translationList.two_factor_disable, 'Dezactivează Autentificarea în Doi Pași (2FA)')
                        }}</h3>
                      <p class="font-weight-regular">
                        {{
                          translation(translationList.two_factor_disable_helper, 'Introdu cheia de recuperare pentru a elimina autentificarea în doi pași.')
                        }}
                      </p>
                    </v-col>
                  </v-row>
                </div>

                <div class="flex-grow-1 d-flex align-center">
                  <v-row class="d-flex justify-center">
                    <v-col cols="12" sm="6" md="6" class="d-flex flex-column justify-center centered-on-mobile">
                      <auth-recovery-key v-model="recoveryKey"/>
                    </v-col>
                  </v-row>
                </div>

                <div>
                  <v-row class="mb-15">
                    <v-col cols="12" sm="12" md="12" class="d-flex justify-center centered-on-mobile">
                      <v-btn
                          class="block-on-mobile"
                          color="primary"
                          flat
                          size="x-large"
                          rounded="lg"
                          @click="removeTwoFactor()"
                      >
                        {{ translation(translationList.two_factor_remove, 'Elimină Two-Factor') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-card>
          </v-container>
        </v-container>
      </v-container>
    </v-container>
  </master-layout>
</template>

<style scoped>

</style>
